import { makeReference, useApolloClient, useQuery } from '@apollo/client';
// import { stopSessionReplay } from '@application/datadog';
import { useAuthState } from '@application/state';
import type { AccountType } from '@application/types';
import type { CurrentAccountQuery } from '@gql/graphql';
import type { MtSocialProviderType } from '@presentation/components';
import { useCallback, useMemo } from 'react';
import { PROVIDER_TYPE_TO_FRONTEND_TYPE } from './constants';
import { useClearAccount } from './useClearAccount';
import { CURRENT_ACCOUNT } from './useGetAccount';

export const SOCIAL_AUTH_PROVIDERS = ['google.com', 'facebook.com'];

export const useCurrentAccount = () => {
  const apolloClient = useApolloClient();
  const { clearAccount, redirectOnTokenErrorHandler } = useClearAccount();

  const authState = useAuthState();

  const { data, loading, error } = useQuery(CURRENT_ACCOUNT, {
    // notifyOnNetworkStatusChange: true,
    skip: !authState.token,
    onError: redirectOnTokenErrorHandler,
  });

  const currentAccountLogout = useCallback(() => {
    // stopSessionReplay();
    clearAccount();
  }, [clearAccount]);

  const currentAccountType = useMemo((): AccountType => {
    if (data?.accountMe?.dogBreeder != null) {
      return 'breeder';
    }
    return 'buyer';
  }, [data?.accountMe?.dogBreeder]);

  const currentAccountLoggedIn = useMemo(() => {
    return !!data?.accountMe;
  }, [data?.accountMe]);

  const currentAccountUpdateCache = useCallback(
    (
      newAccount: CurrentAccountQuery['accountMe'] | undefined | null,
      token: string | undefined | null,
    ) => {
      if (!newAccount) return;
      if (!token || !newAccount) {
        // stopSessionReplay();
        return clearAccount();
      }

      if (token) {
        authState.setState({ token });
      }

      apolloClient.cache.modify({
        id: apolloClient.cache.identify(makeReference('ROOT_QUERY')),
        fields: {
          accountMe: () => {
            // console.log(existing, toReference);
            return newAccount;
          },
        },
        optimistic: true,
      });
    },
    [apolloClient.cache, authState, clearAccount],
  );

  const currentAccountAuthType = useMemo((): MtSocialProviderType | undefined => {
    const type = authState.token?.split(':::')[0];
    return PROVIDER_TYPE_TO_FRONTEND_TYPE[type as keyof typeof PROVIDER_TYPE_TO_FRONTEND_TYPE];
  }, [authState.token]);

  const currentAccount = useMemo(() => {
    return currentAccountLoggedIn ? data?.accountMe : undefined;
  }, [currentAccountLoggedIn, data?.accountMe]);

  return {
    currentAccount,
    currentAccountLoggedIn,
    currentAccountLoading: loading,
    currentAccountError: error,
    currentAccountLogout,
    currentAccountType,
    currentAccountUpdateCache,
    currentAccountAuthType,
    currentAccountCheckoutPriceValue: 29, // was use to AB test pricing
    currentAccountClear: clearAccount,
  };
};
